/**
 * BroadcastStore
 *
 * A wrapper for SolidJS stores that enables synchronization across browser tabs
 * using the Broadcast Channel API.
 *
 * Usage Examples:
 *
 * // Create a new broadcast store
 * const [userSettings, setUserSettings, setUserSettingsBroadcastChannel] = createBroadcastStore(
 *   { theme: "light", language: "en" }
 * );
 * In a component so that the internal onCleanup is valid:
 * setUserSettingsBroadcastChannel("user-settings")
 * The BroadcastChannel is created only after that call.
 */

import { onCleanup } from "solid-js";
import { createStore, unwrap, reconcile } from "solid-js/store";
import type { SetStoreFunction, Store } from "solid-js/store";

interface BroadcastMessage<T = any> {
  type: "store-update";
  data: T;
  timestamp: number;
}

/**
 * Creates a broadcast-enabled SolidJS store that synchronizes across browser tabs.
 *
 * @param initialValue - Initial value for the store
 * @returns Tuple of [store, setStore, setStoreBroadcastChannel] where setStore broadcasts changes
 * after you called setStoreBroadcastChannel
 */
export function createBroadcastStore<T extends Record<string, any>>(
  initialValue: T
): [Store<T>, SetStoreFunction<T>, (channelName: string) => void] {
  // Create the SolidJS store
  const [store, setStoreInternal] = createStore<T>(initialValue);

  // State for broadcast channel management
  let channel: BroadcastChannel | null = null;
  let isReceivingExternalUpdate = false;
  let currentChannelName: string | null = null;

  // Function to clean up existing channel
  const cleanupChannel = () => {
    if (channel) {
      channel.removeEventListener("message", handleMessage);
      channel.close();
      channel = null;
      currentChannelName = null;
    }
  };

  // Listen for updates from other tabs
  const handleMessage = (event: MessageEvent<BroadcastMessage<T>>) => {
    if (event.data.type !== "store-update") {
      return;
    }

    // Set flag to prevent broadcasting this update back
    isReceivingExternalUpdate = true;
    setStoreInternal(reconcile(event.data.data));
    isReceivingExternalUpdate = false;
  };

  let cleanupAdded = false;
  // Function to set/change the broadcast channel
  const setBroadcastChannel = (channelName: string) => {
    // Don't recreate if it's the same channel
    if (currentChannelName === channelName) {
      return;
    }

    // Clean up existing channel
    cleanupChannel();

    // Check if BroadcastChannel is supported
    if (typeof BroadcastChannel === "undefined") {
      console.warn("BroadcastChannel is not supported in this browser. Store will work locally only.");
      return;
    }

    // Create new broadcast channel
    channel = new BroadcastChannel(channelName);
    currentChannelName = channelName;
    channel.addEventListener("message", handleMessage);

    if (!cleanupAdded) {
      onCleanup(() => {
        cleanupChannel();
      });
      cleanupAdded = true;
    }
  };

  // Wrapped setStore that broadcasts changes
  const setStore: SetStoreFunction<T> = (...args: any[]) => {
    // Apply the update to the local store first
    (setStoreInternal as any)(...args);

    // If this update came from another tab, don't broadcast it back
    if (isReceivingExternalUpdate) {
      return;
    }

    // Only broadcast if we have an active channel
    if (!channel) {
      return;
    }

    // Broadcast the updated store state to other tabs
    const message: BroadcastMessage<T> = {
      type: "store-update",
      data: unwrap(store),
      timestamp: Date.now(),
    };

    channel.postMessage(message);
  };

  return [store, setStore, setBroadcastChannel];
}
